import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { Edit, ArrowLeft, User, Phone, Mail, MapPin, UserCheck, CreditCard, Search, X, AlertCircle, Plus, Minus, Check, Save , RotateCcw} from 'lucide-react';
import patientService from '../../patient/services/patientService';

const ClinicianPatientDetailsPage = () => {
  const { patientId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if patient data was passed via navigation state
  const passedPatientData = location.state?.patientData;
  
  // State for patient details
  const [patient, setPatient] = useState(null);
  const [patientAccountsDetails, setPatientAccountsDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  // State for search inputs (editable)
  const [searchData, setSearchData] = useState({
    patientId: '',
    patientName: '',
    patientDOB: ''
  });

  // State for patient search and selection
  const [showPatientIdDropdown, setShowPatientIdDropdown] = useState(false);
  const [showPatientNameDropdown, setShowPatientNameDropdown] = useState(false);
  const [filteredPatientsById, setFilteredPatientsById] = useState([]);
  const [filteredPatientsByName, setFilteredPatientsByName] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [isPatientSelected, setIsPatientSelected] = useState(false);

  // State for real patient data from API
  const [allPatients, setAllPatients] = useState([]);
  const [loadingPatients, setLoadingPatients] = useState(false);

  // State for toast notifications
  const [toast, setToast] = useState(null);

  // State for collapsible search section (closed by default, except when auto-populated)
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  
  // State to track if this is auto-populated from navigation
  const [isAutoPopulated, setIsAutoPopulated] = useState(false);

  // State for collapsible detail sections
  const [isGeneralDetailsExpanded, setIsGeneralDetailsExpanded] = useState(true);
  const [isContactDetailsExpanded, setIsContactDetailsExpanded] = useState(false);
  const [isNextOfKinDetailsExpanded, setIsNextOfKinDetailsExpanded] = useState(false);
  const [isOtherDetailsExpanded, setIsOtherDetailsExpanded] = useState(false);

  // State for editing functionality
  const [editingSection, setEditingSection] = useState(null);
  const [backupData, setBackupData] = useState({});
  
  // Editable data states
  const [editablePatient, setEditablePatient] = useState({
    patient_id: '',
    first_name: '',
    last_name: '',
    dob: '',
    age: '',
    gender: '',
    phone: '',
    user_email: '',
    address: '',
    suburb: '',
    state: '',
    postcode: '',
    country: '',
    nok_name: '',
    nok_relation: '',
    nok_phone: '',
    nok_email: ''
  });
  
  const [editableAccountsDetails, setEditableAccountsDetails] = useState({
    medicare_number: '',
    medicare_expiry_date: '',
    pensioncard_number: '',
    pensioncard_expiry_date: ''
  });

  // Set page title and fetch patient data
  useEffect(() => {
    document.title = 'MediFusion - Patient Files';
    fetchAllPatients();
    
    // If patient data was passed via navigation, auto-populate and fetch details
    if (passedPatientData) {
      console.log('🎯 Patient data passed via navigation:', passedPatientData);
      handleDirectPatientAccess(passedPatientData);
    } else {
      fetchPatientData();
    }
  }, [patientId, passedPatientData]);

  // Handle direct patient access from navigation (skip search flow)
  const handleDirectPatientAccess = async (patientData) => {
    try {
      console.log('🚀 Auto-loading patient details for:', patientData.patient_id);
      
      // Mark as auto-populated
      setIsAutoPopulated(true);
      
      // Auto-populate search fields
      setSearchData({
        patientId: patientData.patient_id,
        patientName: patientData.patient_name,
        patientDOB: 'Auto-populated from appointment'
      });
      
      // Mark as selected and show search (but collapsed after data loads)
      setIsPatientSelected(true);
      setIsSearchExpanded(true); // Show initially so user can see auto-filled data
      
      // Fetch full patient details automatically
      await fetchPatientData();
      
      // Collapse search after successful load to focus on patient details
      setTimeout(() => {
        setIsSearchExpanded(false);
      }, 2000);
      
      console.log('✅ Auto-populated patient file successfully');
    } catch (error) {
      console.error('❌ Error in direct patient access:', error);
      showToast('Failed to load patient details automatically', 'error');
    }
  };

  // Update search fields when patient data is loaded
  useEffect(() => {
    if (patient) {
      setSearchData(prev => ({
        ...prev,
        patientId: patient.patient_id || prev.patientId,
        patientName: `${patient.first_name} ${patient.last_name}` || prev.patientName,
        patientDOB: patient.dob || prev.patientDOB
      }));
      setSelectedPatient(patient);
      setIsPatientSelected(true);
    }
  }, [patient]);

  // Fetch patient data from API
  const fetchPatientData = async () => {
    try {
      setLoading(true);
      console.log('🔍 Fetching patient details for ID:', patientId);
      
      // Try to fetch patient by patient_id (string format like P001)
      const patientData = await patientService.getPatientByPatientId(patientId);
      console.log('👤 Patient data from API:', patientData);
      console.log('📧 Patient user_email field:', patientData.user_email);
      console.log('📧 Patient email field:', patientData.email);
      console.log('👤 Patient user object:', patientData.user);
      
      setPatient(patientData);
      
      // Populate editable patient data
      setEditablePatient({
        patient_id: patientData.patient_id || '',
        first_name: patientData.first_name || '',
        last_name: patientData.last_name || '',
        dob: patientData.dob || '',
        age: patientData.age || '',
        gender: patientData.gender || '',
        phone: patientData.phone || '',
        user_email: patientData.user_email || patientData.email || '',
        address: patientData.address || '',
        suburb: patientData.suburb || '',
        state: patientData.state || '',
        postcode: patientData.postcode || '',
        country: patientData.country || '',
        nok_name: patientData.nok_name || '',
        nok_relation: patientData.nok_relation || '',
        nok_phone: patientData.nok_phone || '',
        nok_email: patientData.nok_email || ''
      });

      // Fetch patient accounts details if patient has numeric ID
      if (patientData?.id) {
        try {
          console.log('🔍 Attempting to fetch accounts details for patient ID:', patientData.id);
          const accountsData = await patientService.getPatientAccountsDetailsByPatientId(patientData.id);
          console.log('💼 Patient accounts details successfully fetched:', accountsData);
          setPatientAccountsDetails(accountsData);
          
          // Populate editable accounts details
          setEditableAccountsDetails({
            medicare_number: accountsData.medicare_number || '',
            medicare_expiry_date: accountsData.medicare_expiry_date || '',
            pensioncard_number: accountsData.pensioncard_number || '',
            pensioncard_expiry_date: accountsData.pensioncard_expiry_date || ''
          });
        } catch (error) {
          console.log('ℹ️ No accounts details found for patient:', error.message);
          setPatientAccountsDetails(null);
        }
      } else {
        console.log('⚠️ Patient data missing numeric ID field for accounts details lookup');
        setPatientAccountsDetails(null);
      }
    } catch (error) {
      console.error('❌ Error fetching patient data:', error);
      setPatient(null);
      setPatientAccountsDetails(null);
    } finally {
      setLoading(false);
    }
  };

  // Fetch all patients from the database
  const fetchAllPatients = async () => {
    try {
      setLoadingPatients(true);
      console.log('🔍 Fetching all patients from database...');
      const response = await patientService.getAllPatients();
      console.log('👥 Patients from API:', response);
      
      // The response should have a patients array and metadata
      const patients = response.patients || response || [];
      setAllPatients(patients);
      console.log('✅ Loaded', patients.length, 'patients from database');
    } catch (error) {
      console.error('❌ Error fetching patients:', error);
      // Fallback to empty array if API fails
      setAllPatients([]);
    } finally {
      setLoadingPatients(false);
    }
  };

  // Filter patients by ID
  const filterPatientsById = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsById([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      patient.patient_id && patient.patient_id.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsById(filtered);
  };

  // Filter patients by name
  const filterPatientsByName = (searchTerm) => {
    if (!searchTerm.trim()) {
      setFilteredPatientsByName([]);
      return;
    }

    const filtered = allPatients.filter(patient => 
      `${patient.first_name} ${patient.last_name}`.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredPatientsByName(filtered);
  };

  // Handle patient ID input change
  const handlePatientIdChange = (value) => {
    setSearchData(prev => ({ ...prev, patientId: value }));
    filterPatientsById(value);
    if (value) {
      setShowPatientIdDropdown(true);
      setShowPatientNameDropdown(false);
    }
  };

  // Handle patient name input change
  const handlePatientNameChange = (value) => {
    setSearchData(prev => ({ ...prev, patientName: value }));
    filterPatientsByName(value);
    if (value) {
      setShowPatientNameDropdown(true);
      setShowPatientIdDropdown(false);
    }
  };

  // Handle patient selection from dropdown
  const handlePatientSelect = (patient) => {
    setSelectedPatient(patient);
    
    // Format DOB for the date input field
    let formattedDOB = '';
    if (patient.dob && patient.dob !== '1900-01-01' && patient.dob !== null) {
      formattedDOB = patient.dob; // API already returns YYYY-MM-DD format
      console.log(`✅ DOB populated for ${patient.first_name} ${patient.last_name}: ${patient.dob}`);
    } else {
      console.log(`⚠️ No valid DOB found for ${patient.first_name} ${patient.last_name}`);
    }
    
    setSearchData({
      patientId: patient.patient_id,
      patientName: `${patient.first_name} ${patient.last_name}`,
      patientDOB: formattedDOB
    });
    setIsPatientSelected(true);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Show toast notification
  const showToast = (message, type = 'error') => {
    setToast({ message, type });
    // Auto-hide toast after 4 seconds
    setTimeout(() => {
      setToast(null);
    }, 4000);
  };

  // Handle search
  const handleSearch = (e) => {
    e.preventDefault();
    
    // If auto-populated, skip search validation
    if (isAutoPopulated) {
      console.log('✅ Search skipped - already auto-populated and validated');
      return;
    }
    
    if (!selectedPatient) {
      showToast('Please select a patient first', 'error');
      return;
    }

    if (!searchData.patientDOB) {
      showToast('Please enter the patient\'s date of birth', 'error');
      return;
    }

    // Check if DOB matches
    if (searchData.patientDOB === selectedPatient.dob) {
      // Navigate to patient details page
      navigate(`/clinician/patientfiles/${selectedPatient.patient_id}/general_details`);
    } else {
      showToast('DOB does not match for the selected patient. Please verify and try again.', 'error');
    }
  };

  // Handle new patient button
  const handleNewPatient = () => {
    setSearchData({
      patientId: '',
      patientName: '',
      patientDOB: ''
    });
    setSelectedPatient(null);
    setIsPatientSelected(false);
    setShowPatientIdDropdown(false);
    setShowPatientNameDropdown(false);
    setFilteredPatientsById([]);
    setFilteredPatientsByName([]);
  };

  // Calculate age from DOB
  const calculateAge = (dob) => {
    const today = new Date();
    const birthDate = new Date(dob);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Handle edit button click
  const handleEditRecord = (section) => {
    setEditingSection(section);
    
    // Backup current data for cancel functionality
    let currentData = {};
    switch (section) {
      case 'general':
        currentData = { ...editablePatient };
        break;
      case 'contact':
        currentData = { ...editablePatient };
        break;
      case 'nextOfKin':
        currentData = { ...editablePatient };
        break;
      case 'other':
        currentData = { ...editableAccountsDetails };
        break;
      default:
        break;
    }
    setBackupData(currentData);
  };

  // Handle save changes
  const handleSaveChanges = async (section) => {
    try {
      setEditingSection(null);
      
      if (!patient) {
        showToast('No patient selected', 'error');
        return;
      }

      const token = localStorage.getItem('authToken');
      const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

      if (section === 'general' || section === 'contact' || section === 'nextOfKin') {
        // Update patient details
        const updateData = {
          first_name: editablePatient.first_name || patient.first_name,
          last_name: editablePatient.last_name || patient.last_name,
          dob: editablePatient.dob || patient.dob,
          gender: editablePatient.gender || patient.gender,
          height: editablePatient.height || patient.height,
          weight: editablePatient.weight || patient.weight,
          phone: editablePatient.phone || patient.phone,
          address: editablePatient.address || patient.address,
          suburb: editablePatient.suburb || patient.suburb,
          state: editablePatient.state || patient.state,
          postcode: editablePatient.postcode || patient.postcode,
          country: editablePatient.country || patient.country || "Australia",
          nok_name: editablePatient.nok_name || patient.nok_name,
          nok_relation: editablePatient.nok_relation || patient.nok_relation,
          nok_phone: editablePatient.nok_phone || patient.nok_phone,
          nok_email: editablePatient.nok_email || patient.nok_email,
          is_active: editablePatient.is_active !== undefined ? editablePatient.is_active : patient.is_active
        };

        console.log('Sending patient update data:', updateData);

        const response = await fetch(`${apiBaseUrl}/patients/${patient.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(updateData)
        });

        console.log('Patient update response status:', response.status);

        if (response.ok) {
          const updatedPatient = await response.json();
          setPatient(updatedPatient);
          showToast('Patient details updated successfully', 'success');
        } else {
          const errorData = await response.json().catch(() => ({}));
          console.error('Patient update failed:', response.status, errorData);
          throw new Error(`Failed to update patient: ${errorData.detail || 'Unknown error'}`);
        }
      } else if (section === 'other') {
        // Update accounts details
        if (patientAccountsDetails && patientAccountsDetails.id) {
          // Update existing accounts details
          const updateData = {
            medicare_number: editableAccountsDetails.medicareNumber || patientAccountsDetails.medicare_number,
            medicare_expiry_date: editableAccountsDetails.medicareExpiryDate || patientAccountsDetails.medicare_expiry_date,
            pensioncard_number: editableAccountsDetails.pensioncardNumber || patientAccountsDetails.pensioncard_number,
            pensioncard_expiry_date: editableAccountsDetails.pensioncardExpiryDate || patientAccountsDetails.pensioncard_expiry_date,
            private_health_co: editableAccountsDetails.privateHealthCo || patientAccountsDetails.private_health_co,
            private_healthcard_number: editableAccountsDetails.privateHealthcardNumber || patientAccountsDetails.private_healthcard_number,
            private_healthcard_expiry_date: editableAccountsDetails.privateHealthcardExpiryDate || patientAccountsDetails.private_healthcard_expiry_date
          };

          const response = await fetch(`${apiBaseUrl}/patient-accounts-details/${patientAccountsDetails.id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(updateData)
          });

          if (response.ok) {
            const updatedAccountsDetails = await response.json();
            setPatientAccountsDetails(updatedAccountsDetails);
            showToast('Accounts details updated successfully', 'success');
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.error('Accounts details update failed:', response.status, errorData);
            throw new Error(`Failed to update accounts details: ${errorData.detail || 'Unknown error'}`);
          }
        } else {
          // Create new accounts details
          const createData = {
            user_id: patient.user_id,
            patient_id: patient.id,
            medicare_number: editableAccountsDetails.medicareNumber || '',
            medicare_expiry_date: editableAccountsDetails.medicareExpiryDate || null,
            pensioncard_number: editableAccountsDetails.pensioncardNumber || '',
            pensioncard_expiry_date: editableAccountsDetails.pensioncardExpiryDate || null,
            private_health_co: editableAccountsDetails.privateHealthCo || '',
            private_healthcard_number: editableAccountsDetails.privateHealthcardNumber || '',
            private_healthcard_expiry_date: editableAccountsDetails.privateHealthcardExpiryDate || null
          };

          const response = await fetch(`${apiBaseUrl}/patient-accounts-details/`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(createData)
          });

          if (response.ok) {
            const newAccountsDetails = await response.json();
            setPatientAccountsDetails(newAccountsDetails);
            showToast('Accounts details created successfully', 'success');
          } else {
            const errorData = await response.json().catch(() => ({}));
            console.error('Accounts details creation failed:', response.status, errorData);
            throw new Error(`Failed to create accounts details: ${errorData.detail || 'Unknown error'}`);
          }
        }
      }
      
    } catch (error) {
      console.error('Error saving changes:', error);
      showToast(`Failed to save changes: ${error.message}`, 'error');
    }
  };

  // Handle cancel changes
  const handleCancelChanges = (section) => {
    setEditingSection(null);
    
    // Restore backup data
    switch (section) {
      case 'general':
      case 'contact':
      case 'nextOfKin':
        setEditablePatient(backupData);
        break;
      case 'other':
        setEditableAccountsDetails(backupData);
        break;
      default:
        break;
    }
  };

  // Handle input change for editable fields
  const handleInputChange = (field, value, section = 'patient') => {
    if (section === 'accounts') {
      setEditableAccountsDetails(prev => ({
        ...prev,
        [field]: value
      }));
    } else {
      setEditablePatient(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="flex items-center justify-center h-64">
          <div className="flex items-center space-x-2">
            <svg className="animate-spin h-6 w-6 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-gray-600">Loading patient details...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!patient) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="bg-white rounded-lg shadow-md p-6 text-center">
          <h2 className="text-xl font-bold text-gray-800 mb-4">Patient Not Found</h2>
          <p className="text-gray-600 mb-4">The patient with ID {patientId} could not be found.</p>
          <Link
            to="/clinician/patientfiles"
            className="bg-blue-600 text-white px-6 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Back to Patient Files
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      {/* Top Banner */}
      <div className="bg-blue-600 text-white px-6 py-1.5 rounded-lg mb-6">
        <h1 className="text-lg font-semibold text-center">Patient Files</h1>
      </div>



      {/* White Content Container with Search Block */}
      <div className={`bg-white rounded-lg shadow-md mb-4 ${isSearchExpanded ? "p-4" : "p-3"}`}>
        {/* Search Section */}
        <div className={isSearchExpanded ? "mb-4" : "mb-2"}>
          {/* Search Header with Icon and Toggle */}
          <div className={`flex items-center justify-between ${isSearchExpanded ? "mb-2" : "mb-0"}`}>
            <div className="flex items-center space-x-2">
              <Search className="h-5 w-5 text-blue-600" />
              <h2 className="text-lg font-bold text-gray-800">Patient Search</h2>
            </div>
            <button
              type="button"
              onClick={() => setIsSearchExpanded(!isSearchExpanded)}
              className={`bg-blue-500 hover:bg-blue-600 text-white rounded-lg flex items-center justify-center transition-colors shadow-sm ${isSearchExpanded ? "w-8 h-8" : "w-7 h-7"}`}
            >
              {isSearchExpanded ? (
                <Minus className="h-4 w-4" />
              ) : (
                <Plus className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* Collapsible Content */}
          {isSearchExpanded && (
            <div>
              {/* Blue stylish divider */}
              <div className="h-0.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-full mb-4"></div>
              
              {/* Search Form */}
            <form onSubmit={handleSearch} className="space-y-2">
              {/* Input Fields and Buttons Row - All in One Line */}
              <div className="flex items-end space-x-3">
                {/* Patient ID Input */}
                <div className="flex-1 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
                  <input
                    type="text"
                    placeholder="Enter Patient ID"
                    value={searchData.patientId}
                    onChange={(e) => handlePatientIdChange(e.target.value)}
                    onFocus={() => setShowPatientIdDropdown(true)}
                    disabled={isPatientSelected}
                    className={`w-full px-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm ${
                      isPatientSelected && isAutoPopulated
                        ? 'bg-green-50 border-green-300 text-green-700'
                        : isPatientSelected
                        ? 'bg-gray-100 border-gray-300'
                        : 'border-gray-300'
                    } disabled:cursor-not-allowed`}
                  />
                  {isAutoPopulated && isPatientSelected && (
                    <div className="absolute top-1 right-1 text-green-600">
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                  
                  {/* Dropdown for patient selection by ID */}
                  {showPatientIdDropdown && filteredPatientsById.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {filteredPatientsById.map((patient) => (
                        <div
                          key={patient.id || patient.patient_id}
                          onClick={() => handlePatientSelect(patient)}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{patient.patient_id}</div>
                          <div className="text-sm text-gray-600">{patient.first_name} {patient.last_name}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Patient Name Input */}
                <div className="flex-1 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                  <input
                    type="text"
                    placeholder="Enter Patient Name"
                    value={searchData.patientName}
                    onChange={(e) => handlePatientNameChange(e.target.value)}
                    onFocus={() => setShowPatientNameDropdown(true)}
                    disabled={isPatientSelected}
                    className={`w-full px-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm ${
                      isPatientSelected && isAutoPopulated
                        ? 'bg-green-50 border-green-300 text-green-700'
                        : isPatientSelected
                        ? 'bg-gray-100 border-gray-300'
                        : 'border-gray-300'
                    } disabled:cursor-not-allowed`}
                  />
                  {isAutoPopulated && isPatientSelected && (
                    <div className="absolute top-1 right-1 text-green-600">
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                  
                  {/* Dropdown for patient selection by name */}
                  {showPatientNameDropdown && filteredPatientsByName.length > 0 && (
                    <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-50 max-h-60 overflow-y-auto">
                      {filteredPatientsByName.map((patient) => (
                        <div
                          key={patient.id || patient.patient_id}
                          onClick={() => handlePatientSelect(patient)}
                          className="px-4 py-2 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors"
                        >
                          <div className="font-medium text-gray-900">{patient.first_name} {patient.last_name}</div>
                          <div className="text-sm text-gray-500">ID: {patient.patient_id}</div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                
                {/* Patient DOB Input */}
                <div className="w-48 relative">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient DOB:</label>
                  <input
                    type={isAutoPopulated ? "text" : "date"}
                    value={searchData.patientDOB}
                    onChange={(e) => setSearchData(prev => ({ ...prev, patientDOB: e.target.value }))}
                    disabled={isAutoPopulated}
                    readOnly={isAutoPopulated}
                    className={`w-full px-2 py-1.5 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors text-sm ${
                      isAutoPopulated
                        ? 'bg-green-50 border-green-300 text-green-700'
                        : 'border-gray-300'
                    } disabled:cursor-not-allowed`}
                  />
                  {isAutoPopulated && (
                    <div className="absolute top-1 right-1 text-green-600">
                      <Check className="h-4 w-4" />
                    </div>
                  )}
                </div>
                
                {/* Search Button */}
                {isAutoPopulated ? (
                  <div className="bg-green-500 text-white px-4 py-1.5 rounded-md flex items-center space-x-2 shadow-sm h-8 text-sm">
                    <Check className="h-3 w-3" />
                    <span>Auto-loaded</span>
                  </div>
                ) : (
                  <button
                    type="submit"
                    className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
                  >
                    <Search className="h-3 w-3" />
                    <span>Search</span>
                  </button>
                )}
                
                {/* Reset Fields Button */}
                <button
                  type="button"
                  onClick={handleNewPatient}
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 hover:bg-blue-600 text-white px-4 py-1.5 rounded-md transition-colors duration-200 flex items-center space-x-2 shadow-sm hover:shadow-md h-8 text-sm"
                >
                <RotateCcw className="h-3 w-3" />
                <span>Reset Fields</span>
              </button>
              </div>
            </form>
            </div>
          )}
        </div>
      </div>

      {/* Patient Name Header */}
      <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-3 rounded-lg mb-6 shadow-sm relative overflow-hidden">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 right-0 w-24 h-24 bg-blue-200 rounded-full -translate-y-12 translate-x-12"></div>
          <div className="absolute bottom-0 right-0 w-16 h-16 bg-blue-300 rounded-full translate-y-8 translate-x-8"></div>
        </div>
        
        {/* Patient name with enhanced styling */}
        <div className="relative z-10">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-5 bg-blue-500 rounded-full"></div>
            <div>
              <h2 className="text-lg font-bold text-gray-900 uppercase tracking-wide leading-tight">
                {patient.first_name} {patient.last_name}
              </h2>
              <div className="h-0.5 w-12 bg-blue-500 mt-1 rounded-full"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Left Navigation Sidebar */}
      <div className="flex gap-6">
        <div className="w-64">
          <div className="bg-white rounded-lg shadow-md p-4">

            <nav className="space-y-2">
              <Link 
                to={`/clinician/patientfiles/${patientId}/bookings`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Bookings
              </Link>
              <button className="w-full text-left px-3 py-2 rounded-md bg-blue-600 text-white transition-colors">
                General Details
              </button>
              <Link 
                to={`/clinician/patientfiles/${patientId}/medical_details`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Medical Details
              </Link>
              <Link 
                to={`/clinician/patientfiles/${patientId}/history`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                History
              </Link>
              <Link 
                to={`/clinician/patientfiles/${patientId}/analytics`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Analytics
              </Link>
              <Link 
                to={`/clinician/patientfiles/${patientId}/documents`}
                className="w-full text-left px-3 py-2 rounded-md text-gray-600 hover:bg-gray-50 transition-colors block"
              >
                Documents
              </Link>
            </nav>
          </div>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 space-y-6">
          {/* General Details Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <User className="h-5 w-5" />
                  <h4 className="font-semibold">General Details</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsGeneralDetailsExpanded(!isGeneralDetailsExpanded)}
                  className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
                >
                  {isGeneralDetailsExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            {isGeneralDetailsExpanded && (
            <div className="p-4">
              {/* Single row layout for all input fields */}
              <div className="flex gap-3">
                {/* Patient ID - smaller width */}
                <div className="flex-shrink-0 w-32">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient ID:</label>
                  <input
                    type="text"
                    value={editingSection === 'general' ? editablePatient.patient_id : patient.patient_id}
                    onChange={(e) => handleInputChange('patient_id', e.target.value)}
                    readOnly={editingSection !== 'general'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Patient Name - increased width by 20% */}
                <div className="flex-shrink-0 w-64">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Patient Name:</label>
                  <input
                    type="text"
                    value={editingSection === 'general' ? `${editablePatient.first_name} ${editablePatient.last_name}` : `${patient.first_name} ${patient.last_name}`}
                    onChange={(e) => {
                      const [firstName, ...lastNameParts] = e.target.value.split(' ');
                      handleInputChange('first_name', firstName || '');
                      handleInputChange('last_name', lastNameParts.join(' ') || '');
                    }}
                    readOnly={editingSection !== 'general'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Date of Birth - increased width */}
                <div className="flex-shrink-0 w-48">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date Of Birth:</label>
                  <input
                    type="date"
                    value={editingSection === 'general' ? editablePatient.dob : patient.dob}
                    onChange={(e) => handleInputChange('dob', e.target.value)}
                    readOnly={editingSection !== 'general'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'general' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Age - increased width */}
                <div className="flex-shrink-0 w-28">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Age:</label>
                  <input
                    type="text"
                    value={editingSection === 'general' ? calculateAge(editablePatient.dob) : calculateAge(patient.dob)}
                    readOnly
                    className="w-full px-3 py-1 bg-gray-50 border border-gray-300 rounded-md text-gray-900"
                  />
                </div>

                {/* Gender - increased width */}
                <div className="flex-shrink-0 w-32">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Gender:</label>
                  <input
                    type="text"
                    value={patient.gender || "Not specified"}
                    readOnly
                    className="w-full px-3 py-1 bg-gray-50 border border-gray-300 rounded-md text-gray-900"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4 space-x-2">
                {editingSection !== 'general' ? (
                  <button 
                    onClick={() => handleEditRecord('general')}
                    className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Record</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelChanges('general')}
                      className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={() => handleSaveChanges('general')}
                      className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Contact Details Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Phone className="h-5 w-5" />
                  <h4 className="font-semibold">Contact Details</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsContactDetailsExpanded(!isContactDetailsExpanded)}
                  className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
                >
                  {isContactDetailsExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            {isContactDetailsExpanded && (
            <div className="p-4">
              {/* Two-row layout for Contact Details */}
              <div className="space-y-3">
                {/* First Row: Phone and Email */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone:</label>
                    <input
                      type="text"
                      value={editingSection === 'contact' ? editablePatient.phone : patient.phone}
                      onChange={(e) => handleInputChange('phone', e.target.value)}
                      readOnly={editingSection !== 'contact'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                        editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
                    <input
                      type="text"
                      value={editingSection === 'contact' ? editablePatient.user_email : (patient.user_email || patient.email || 'Not provided')}
                      onChange={(e) => handleInputChange('user_email', e.target.value)}
                      readOnly={editingSection !== 'contact'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                        editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>
                </div>

                {/* Second Row: Address, Suburb, State, Postcode, Country */}
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Address:</label>
                    <input
                      type="text"
                      value={editingSection === 'contact' ? editablePatient.address : patient.address}
                      onChange={(e) => handleInputChange('address', e.target.value)}
                      readOnly={editingSection !== 'contact'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                        editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>
                  <div className="flex-shrink-0 w-32">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Suburb:</label>
                    <input
                      type="text"
                      value={editingSection === 'contact' ? editablePatient.suburb : patient.suburb}
                      onChange={(e) => handleInputChange('suburb', e.target.value)}
                      readOnly={editingSection !== 'contact'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                        editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>
                  <div className="flex-shrink-0 w-24">
                    <label className="block text-sm font-medium text-gray-700 mb-1">State:</label>
                    <input
                      type="text"
                      value={editingSection === 'contact' ? editablePatient.state : patient.state}
                      onChange={(e) => handleInputChange('state', e.target.value)}
                      readOnly={editingSection !== 'contact'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                        editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>
                  <div className="flex-shrink-0 w-28">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Postcode:</label>
                    <input
                      type="text"
                      value={editingSection === 'contact' ? editablePatient.postcode : patient.postcode}
                      onChange={(e) => handleInputChange('postcode', e.target.value)}
                      readOnly={editingSection !== 'contact'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                        editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>
                  <div className="flex-shrink-0 w-32">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Country:</label>
                    <input
                      type="text"
                      value={editingSection === 'contact' ? editablePatient.country : patient.country}
                      onChange={(e) => handleInputChange('country', e.target.value)}
                      readOnly={editingSection !== 'contact'}
                      className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                        editingSection === 'contact' ? 'bg-white' : 'bg-gray-50'
                      }`}
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end mt-4 space-x-2">
                {editingSection !== 'contact' ? (
                  <button 
                    onClick={() => handleEditRecord('contact')}
                    className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Record</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelChanges('contact')}
                      className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={() => handleSaveChanges('contact')}
                      className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Next Of Kin Details Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <UserCheck className="h-5 w-5" />
                  <h4 className="font-semibold">Next Of Kin Details</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsNextOfKinDetailsExpanded(!isNextOfKinDetailsExpanded)}
                  className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
                >
                  {isNextOfKinDetailsExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            {isNextOfKinDetailsExpanded && (
            <div className="p-4">
              {/* Single row layout for all Next of Kin fields */}
              <div className="flex gap-3">
                {/* Next of Kin Name */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Next Of Kin Name:</label>
                  <input
                    type="text"
                    value={editingSection === 'nextOfKin' ? editablePatient.nok_name : (patient.nok_name || 'Not provided')}
                    onChange={(e) => handleInputChange('nok_name', e.target.value)}
                    readOnly={editingSection !== 'nextOfKin'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'nextOfKin' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Next of Kin Relationship */}
                <div className="flex-shrink-0 w-40">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship:</label>
                  <input
                    type="text"
                    value={editingSection === 'nextOfKin' ? editablePatient.nok_relation : (patient.nok_relation || 'Not specified')}
                    onChange={(e) => handleInputChange('nok_relation', e.target.value)}
                    readOnly={editingSection !== 'nextOfKin'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'nextOfKin' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Next of Kin Phone */}
                <div className="flex-shrink-0 w-44">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone:</label>
                  <input
                    type="text"
                    value={editingSection === 'nextOfKin' ? editablePatient.nok_phone : (patient.nok_phone || 'Not provided')}
                    onChange={(e) => handleInputChange('nok_phone', e.target.value)}
                    readOnly={editingSection !== 'nextOfKin'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'nextOfKin' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Next of Kin Email */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email:</label>
                  <input
                    type="text"
                    value={editingSection === 'nextOfKin' ? editablePatient.nok_email : (patient.nok_email || 'Not provided')}
                    onChange={(e) => handleInputChange('nok_email', e.target.value)}
                    readOnly={editingSection !== 'nextOfKin'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'nextOfKin' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>
              </div>
              <div className="flex justify-end mt-4 space-x-2">
                {editingSection !== 'nextOfKin' ? (
                  <button 
                    onClick={() => handleEditRecord('nextOfKin')}
                    className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Record</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelChanges('nextOfKin')}
                      className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={() => handleSaveChanges('nextOfKin')}
                      className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>

          {/* Other Details Panel */}
          <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className="bg-blue-600 text-white px-4 py-1.5 rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <CreditCard className="h-5 w-5" />
                  <h4 className="font-semibold">Other Details</h4>
                </div>
                <button
                  type="button"
                  onClick={() => setIsOtherDetailsExpanded(!isOtherDetailsExpanded)}
                  className="bg-white hover:bg-gray-50 text-blue-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-blue-200"
                >
                  {isOtherDetailsExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            {isOtherDetailsExpanded && (
            <div className="p-4">
              {/* Single row layout for all Other Details fields */}
              <div className="flex gap-3">
                {/* Medicare Number */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medicare Number:</label>
                  <input
                    type="text"
                    value={editingSection === 'other' ? editableAccountsDetails.medicare_number : (patientAccountsDetails?.medicare_number || patient.medicare || 'Not provided')}
                    onChange={(e) => handleInputChange('medicare_number', e.target.value, 'accounts')}
                    readOnly={editingSection !== 'other'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'other' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Medicare Expiry Date */}
                <div className="flex-shrink-0 w-40">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Medicare Expiry:</label>
                  <input
                    type="date"
                    value={editingSection === 'other' ? editableAccountsDetails.medicare_expiry_date : (patientAccountsDetails?.medicare_expiry_date || patient.medicare_expiry || '')}
                    onChange={(e) => handleInputChange('medicare_expiry_date', e.target.value, 'accounts')}
                    readOnly={editingSection !== 'other'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'other' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Pension Card Number */}
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pension Card Number:</label>
                  <input
                    type="text"
                    value={editingSection === 'other' ? editableAccountsDetails.pensioncard_number : (patientAccountsDetails?.pensioncard_number || patient.pension_card || 'NA')}
                    onChange={(e) => handleInputChange('pensioncard_number', e.target.value, 'accounts')}
                    readOnly={editingSection !== 'other'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'other' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>

                {/* Pension Card Expiry Date */}
                <div className="flex-shrink-0 w-40">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pension Expiry:</label>
                  <input
                    type="text"
                    value={editingSection === 'other' ? editableAccountsDetails.pensioncard_expiry_date : ((patientAccountsDetails?.pensioncard_number || patient.pension_card) ? 
                      (patientAccountsDetails?.pensioncard_expiry_date || patient.pension_expiry || 'NA') : 
                      'NA')}
                    onChange={(e) => handleInputChange('pensioncard_expiry_date', e.target.value, 'accounts')}
                    readOnly={editingSection !== 'other'}
                    className={`w-full px-3 py-1 border border-gray-300 rounded-md text-gray-900 ${
                      editingSection === 'other' ? 'bg-white' : 'bg-gray-50'
                    }`}
                  />
                </div>
              </div>

              
              <div className="flex justify-end mt-4 space-x-2">
                {editingSection !== 'other' ? (
                  <button 
                    onClick={() => handleEditRecord('other')}
                    className="bg-white text-blue-600 border border-blue-600 px-2 py-0.5 rounded-md hover:bg-blue-50 transition-colors flex items-center space-x-1 text-sm"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Edit Record</span>
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => handleCancelChanges('other')}
                      className="bg-red-500 text-white px-2 py-0.5 rounded-md hover:bg-red-600 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <X className="h-4 w-4" />
                      <span>Cancel</span>
                    </button>
                    <button
                      onClick={() => handleSaveChanges('other')}
                      className="bg-green-600 text-white px-2 py-0.5 rounded-md hover:bg-green-700 transition-colors flex items-center space-x-1 text-sm"
                    >
                      <Save className="h-4 w-4" />
                      <span>Save</span>
                    </button>
                  </>
                )}
              </div>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg flex items-center space-x-3 transition-all duration-300 transform ${
          toast.type === 'error' 
            ? 'bg-red-500 text-white' 
            : 'bg-green-500 text-white'
        }`}>
          <AlertCircle className="h-5 w-5 flex-shrink-0" />
          <span className="font-medium">{toast.message}</span>
          <button
            onClick={() => setToast(null)}
            className="ml-2 hover:bg-black hover:bg-opacity-20 rounded-full p-1 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default ClinicianPatientDetailsPage;
